// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/no-floating-promises, react-hooks/exhaustive-deps, react-hooks/immutability */

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import _ from "lodash";
import moment from "moment";

import { CSVLink } from "react-csv";
import { toast } from "react-toastify";
import { Loader, Segment } from "semantic-ui-react";

import { useAuth0 } from "../../../../auth/auth0";
import { useUserAuth } from "../../../../hooks/useUserAuth";

import * as flightActions from "../../../../redux/actions/flightActions";

import "./styles.css";
import SearchPanel from "../../../Lumber/Search/SearchPanel.tsx";
import { generateStringFilter, getDefaultGrowingSeasons } from "../../../../utilities/search.ts";
import { FlightSearchResults } from "./FlightSearchResults.tsx";

/**
 * @typedef { import("../../../../apis/types/flightTypes").FlightLogData } FlightLogData
 */

const FlightLog = () => {
	const dispatch = useDispatch();
	const { getTokenSilently } = useAuth0();
	const userAuth = useUserAuth();

	const DATE_FORMAT = "MM/DD/YYYY";

	//-- Data sources
	const clientId = useSelector((state) => (state.clients ? state.clients.currentId : null));
	const uploadingOrthos = useSelector((state) => state.orthoUploads);

	//-- Dropdown options\
	const [uasOptions, setUasOptions] = useState([]);
	const [operatorOptions, setOperatorOptions] = useState([]);
	const [growingSeasonOptions, setGrowingSeasonOptions] = useState([]);
	const [farmOptions, setFarmOptions] = useState([]);
	const [cooperatorCompanyOptions, setCooperatorCompanyOptions] = useState([]);

	//-- Selected filter options
	//-- When setting the default time filter to this year or last year, update these dates to getFullYear() (this year) or getFullYear() - 1
	//-- Also update the presetTimeId to either ("thisYear") or ("lastYear")
	//-- e.g.
	const [fromDate, setFromDate] = useState(new Date(new Date().getFullYear() - 1, 0, 1));
	const [toDate, setToDate] = useState(new Date(new Date().getFullYear() - 1, 11, 31));
	const [growingSeasons, setGrowingSeasons] = useState("");
	const [flightName, setFlightName] = useState("");
	const [presetTimeId, setPresetTimeId] = useState("allDates");
	const [searchAllClients, setSearchAllClients] = useState(false);
	const [filters, setFilters] = useState({});

	//-- Search results
	/** @type {[FlightLogData, React.Dispatch<React.SetStateAction<FlightLogData>>]} */
	const [flightLogSearchResults, setFlightLogSearchResults] = useState(null);

	//-- CSV
	const [flightLogFileName, setFlightLogFileName] = useState("FlightLog.csv");
	const [flightLogCsvData, setFlightLogCsvData] = useState(null);

	//-- UI Control
	const [loading, setLoading] = useState(true);
	const [loadingSearchResults, setLoadingSearchResults] = useState(true);
	const [reset, setReset] = useState(true);
	const [resetDate, setResetDate] = useState(true);

	const analysisStatusOptions = useMemo(
		() => [
			{
				key: "Incomplete",
				value: "Incomplete",
				text: "Incomplete",
			},
			{
				key: "Partial",
				value: "Partially Complete",
				text: "Partially Complete",
			},
			{
				key: "Complete",
				value: "Complete",
				text: "Complete",
			},
		],
		[reset],
	);
	const presetTimeOptions = useMemo(
		() => [
			{
				key: "thisYear",
				value: "thisYear",
				text: "This Year",
			},
			{
				key: "thisMonth",
				value: "thisMonth",
				text: "This Month",
			},
			{
				key: "thisWeek",
				value: "thisWeek",
				text: "This Week",
			},
			{
				key: "lastYear",
				value: "lastYear",
				text: "Last Year",
			},
			{
				key: "lastMonth",
				value: "lastMonth",
				text: "Last Month",
			},
			{
				key: "lastWeek",
				value: "lastWeek",
				text: "Last Week",
			},
			{
				key: "custom",
				value: "custom",
				text: "Custom",
			},
			{
				key: "allDates",
				value: "allDates",
				text: "All Dates",
			},
		],
		[resetDate],
	);
	const cloudCoverageTypes = useMemo(
		() => [
			{
				key: "NullCoverage",
				value: null,
				text: "Not Set",
			},
			{
				key: "Clear",
				value: "Clear",
				text: "Clear",
			},
			{
				key: "Scattered",
				value: "Scattered",
				text: "Scattered",
			},
			{
				key: "Broken",
				value: "Broken",
				text: "Broken",
			},
			{
				key: "Overcast",
				value: "Overcast",
				text: "Overcast",
			},
		],
		[reset],
	);

	// const uploadedAndAnalyzedStatusOptions = [
	// 	{
	// 		key: "all",
	// 		value: "all",
	// 		text: "All"
	// 	},
	// 	{
	// 		key: "uploaded",
	// 		value: "uploaded",
	// 		text: "Uploaded and Not Analyzed"
	// 	},
	// 	{
	// 		key: "analyzed",
	// 		value: "analyzed",
	// 		text: "Uploaded and Analyzed"
	// 	}
	// ];

	useEffect(() => {
		if (userAuth.isReady && clientId) {
			setLoading(true);
			setLoadingSearchResults(true);
			getFlightLog(clientId);
			setLoading(false);
		}
	}, [userAuth.isReady, clientId, searchAllClients]);

	useEffect(() => {
		if (flightLogSearchResults) {
			setDropdownOptions();
			setFlightLogCsvInfo();
		}
	}, [flightLogSearchResults]);

	// const debouncedGetFlightLog = useCallback(
	// 	_.debounce(async (clientId, allClients) => {
	// 		await getFlightLogOptionData(clientId, allClients);
	// 		// await getFlightLogWithDate(clientId, startDate, endDate, allClients);
	// 	}, 1000),
	// 	[]
	// );

	// useEffect(() => {
	// 	debouncedGetFlightLog(clientId, searchAllClients);
	// 	setLoadingSearchResults(true);
	// }, [clientId, searchAllClients, debouncedGetFlightLog]);

	useEffect(() => {
		setFilters((prev) => ({
			...prev,
			dateAndTime: {
				type: "date",
				startDate: moment(fromDate),
				endDate: moment(toDate),
				applied: presetTimeId !== "allDates",
			},
		}));
	}, [fromDate, toDate, presetTimeId]);

	const handleFilters = {
		farms: generateStringFilter("farms.farmId", setFilters, "farms", true),
		growingSeasons: generateStringFilter("growingSeasons.growingSeasonId", setFilters, "growingSeasons", true),
		operator: generateStringFilter("operator", setFilters),
		analysisStatus: generateStringFilter("plotAnalysis", setFilters, "plotAnalysis", true),
		cloudCoverage: generateStringFilter("cloudCoverage", setFilters, "flightConditions"),
		uas: generateStringFilter("uas", setFilters),
		cooperators: generateStringFilter("cooperators.cooperatorCompanyId", setFilters, "cooperators", true),
	};

	async function getFlightLog(clientId) {
		if (clientId && toDate && fromDate) {
			setLoadingSearchResults(true);
			const accessToken = await getTokenSilently();
			dispatch(flightActions.getFlightLog(clientId, searchAllClients, accessToken))
				.then((res) => {
					setFlightLogsState(res, searchAllClients);
					setLoading(false);
					setLoadingSearchResults(false);
				})
				.catch((err) => {
					toast.error("Error searching for flight logs. Please try again.");
					setLoading(false);
					setLoadingSearchResults(false);
					console.log(err);
				});
		}
	}

	function setDropdownOptions() {
		setReset((prev) => !prev);
		setInitialUasOptions(flightLogSearchResults ?? []);
		setInitialFarmOptions(flightLogSearchResults ?? []);
		setInitialGrowingSeasonOptions(flightLogSearchResults ?? []);
		setInitialOperatorOptions(flightLogSearchResults ?? []);
		setCooperatorCompanyOptionsState(flightLogSearchResults ?? []);
	}

	function setInitialUasOptions(uasData) {
		setUasOptions(
			_.chain(uasData)
				.sortBy("uas")
				.sortedUniqBy("uas")
				.map((uas) => {
					const opt = {
						key: uas.uas,
						value: uas.uas,
						text: uas.uas,
						content: (
							<div>
								<span style={{ fontSize: 14, color: "#000000" }}>{uas.uas}</span>
								{/* <span style={{ float: "right", fontSize: 12, opacity: 0.75 }}>({uas.clientName})</span> */}
							</div>
						),
					};
					return opt;
				})
				.value(),
		);
	}

	function setInitialFarmOptions(farmData) {
		setFarmOptions(
			_.chain(farmData)
				.map((fd) => fd.farms)
				.thru((farms) => _.unionBy(...farms, "name"))
				.sortBy("name")
				.map((farm) => {
					const opt = {
						key: farm.farmId,
						value: farm.farmId,
						text: farm.name,
						content: (
							<div>
								<span style={{ fontSize: 14, color: "#000000" }}>{farm.name}</span>
								<span style={{ float: "right", fontSize: 12, opacity: 0.75 }}>({farm.clientName})</span>
							</div>
						),
					};
					return opt;
				})
				.value(),
		);
	}

	function setInitialGrowingSeasonOptions(growingSeasonData) {
		let tempGsOptions = _.chain(growingSeasonData)
			.map((fd) => fd.growingSeasons)
			.thru((growingSeasons) => _.unionBy(...growingSeasons, "name"))
			.sortBy("name")
			.map((growingSeason) => {
				const opt = {
					key: growingSeason.growingSeasonId,
					value: growingSeason.growingSeasonId,
					text: growingSeason.name,
				};
				return opt;
			})
			.reverse()
			.value();
		setGrowingSeasonOptions(tempGsOptions);

		//-- Set growing season to default gs
		let defaultGrowingSeasons = getDefaultGrowingSeasons();
		let filteredSelections = _.map(
			_.filter(tempGsOptions, (gs) => defaultGrowingSeasons.includes(gs.text)),
			"value",
		);
		let defaultGrowingSeasonSelections = filteredSelections.length > 0 ? filteredSelections : [tempGsOptions[0]?.value];
		setGrowingSeasons(defaultGrowingSeasonSelections);
	}

	function setInitialOperatorOptions(operatorData) {
		setOperatorOptions(
			_.chain(operatorData)
				.sortBy("operator")
				.sortedUniqBy("operator")
				.map((operator) => {
					const opt = {
						key: operator.operatorId,
						value: operator.operator,
						text: operator.operator,
						content: (
							<div style={{ display: "flex", flexDirection: "column" }}>
								<span style={{ fontSize: 14, color: "#000000" }}>{operator.operator}</span>
								{/* <span
									title={operator.emailAddress ? `(${operator.emailAddress})` : null}
									style={{ fontSize: 12, opacity: 0.75, overflow: "hidden", textOverflow: "ellipsis" }}
								>
									{operator.emailAddress ? `(${operator.emailAddress})` : null}
								</span> */}
							</div>
						),
					};
					return opt;
				})
				.value(),
		);
	}

	function setCooperatorCompanyOptionsState(companies) {
		setCooperatorCompanyOptions(
			_.chain(companies)
				.map((fd) => fd.cooperators)
				.thru((cooperators) => _.unionBy(...cooperators, "cooperatorName"))
				.sortBy("cooperatorName")
				.map(({ cooperatorCompanyId, cooperatorName }) => {
					const opt = {
						key: cooperatorCompanyId,
						value: cooperatorCompanyId,
						text: cooperatorName,
						content: (
							<div>
								<span style={{ fontSize: 14, color: "#000000" }}>{cooperatorName}</span>
								{/* <span style={{ float: "right", fontSize: 12, opacity: 0.75 }}>({clientName})</span> */}
							</div>
						),
					};
					return opt;
				})
				.value(),
		);
	}

	function setFlightLogsState(searchResults, searchAllClients) {
		if (searchResults) {
			let flights = searchResults.flights;

			let farmsMap = _.reduce(
				searchResults.farms,
				(acc, curr) => {
					if (curr.flightId in acc) {
						acc[curr.flightId].push(curr);
					} else {
						acc[curr.flightId] = [curr];
					}

					return acc;
				},
				{},
			);

			let cooperatorsMap = _.reduce(
				searchResults.farms,
				(acc, curr) => {
					if (curr.flightId in acc) {
						if (_.some(acc[curr.flightId], (c) => c.cooperatorCompanyId === curr.cooperatorCompanyId)) return acc;
						acc[curr.flightId].push({
							cooperatorCompanyId: curr.cooperatorCompanyId,
							cooperatorName: curr.cooperatorName,
						});
					} else {
						acc[curr.flightId] = [
							{ cooperatorCompanyId: curr.cooperatorCompanyId, cooperatorName: curr.cooperatorName },
						];
					}

					return acc;
				},
				{},
			);

			let growingSeasonsMap = _.reduce(
				searchResults.growingSeasons,
				(acc, curr) => {
					if (curr.flightId in acc) {
						acc[curr.flightId].push(curr);
					} else {
						acc[curr.flightId] = [curr];
					}

					return acc;
				},
				{},
			);

			let orthoImages = _.reduce(
				searchResults.orthoImages,
				(acc, curr) => {
					if (curr.flightId in acc) {
						acc[curr.flightId].push(curr);
					} else {
						acc[curr.flightId] = [curr];
					}

					return acc;
				},
				{},
			);

			_.map(flights, (flight) => {
				//Format dateAndTime
				flight.dateAndTime = moment(flight.dateAndTime).format(DATE_FORMAT + " h:mma");

				flight.farms = farmsMap[flight.flightId];

				flight.cooperators = cooperatorsMap[flight.flightId];

				flight.growingSeasons = growingSeasonsMap[flight.flightId];

				flight.orthoImages = orthoImages[flight.flightId];

				flight.analysisRanTimings = _.find(searchResults.analysisRanTimings, (art) => art.flightId === flight.flightId);

				flight.fieldAddedTimings = _.find(searchResults.fieldAddedTimings, (fat) => fat.flightId === flight.flightId);

				flight.flightParameters = { altitude: flight.altitude, droneSpeed: flight.droneSpeed };
				flight.flightConditions = {
					inFlightPauses: flight.inFlightPauses,
					windSpeed: flight.windSpeed,
					cloudCoverage: flight.cloudCoverage,
				};
				flight.fieldsPlaced = { count: flight.fields, total: searchAllClients ? null : flight.totalFields };
				flight.plotAnalysis = _.every(
					_.filter(flight.orthoImages, (oi) => oi.orthoImageTypeId !== null),
					["plotAnalysis", "Complete"],
				)
					? "Complete"
					: _.some(
								_.filter(flight.orthoImages, (oi) => oi.orthoImageTypeId !== null),
								["plotAnalysis", "Partially Complete"],
						  )
						? "Partially Complete"
						: "Incomplete";

				//search for images being uploaded in redux
				_.map(flight.orthoImages, (ortho) => {
					if (
						uploadingOrthos.some(
							(x) =>
								x.flightId === ortho.flightId &&
								x.sensorId === ortho.sensorId &&
								x.orthoImageTypeId === ortho.orthoImageTypeId,
						)
					) {
						ortho.isUploading = true;
					} else {
						ortho.isUploading = false;
					}
				});
			});

			setFlightLogSearchResults(flights);
		}
	}

	const resetFilter = () => {
		setDropdownOptions();
		setFlightName("");
		setResetDate((prev) => !prev);
		setSearchAllClients(false);
	};

	useEffect(() => {
		if (presetTimeId) {
			// if (presetTimeId !== "custom") {
			// 	setOldFromDate(fromDate);
			// 	setOldToDate(toDate);
			// }

			if (presetTimeId === "thisYear") {
				setFromDate(new Date(new Date().getFullYear(), 0, 1));
				setToDate(new Date(new Date().getFullYear(), 11, 31));
			} else if (presetTimeId === "thisMonth") {
				let lastDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();

				setFromDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
				setToDate(new Date(new Date().getFullYear(), new Date().getMonth(), lastDayOfMonth));
			} else if (presetTimeId === "thisWeek") {
				let currentDay = new Date();
				let diff = currentDay.getDate() - currentDay.getDay() + 1;
				let beginningOfWeek = new Date(new Date(currentDay).setDate(diff - 1));
				let endOfWeek = new Date(new Date(currentDay).setDate(diff + 5));

				setFromDate(beginningOfWeek);
				setToDate(endOfWeek);
			} else if (presetTimeId === "lastYear") {
				setFromDate(new Date(new Date().getFullYear() - 1, 0, 1));
				setToDate(new Date(new Date().getFullYear() - 1, 11, 31));
			} else if (presetTimeId === "lastMonth") {
				let lastDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 0).getDate();
				let year = new Date().getFullYear();
				if (new Date().getMonth() === 1) {
					year = new Date().getFullYear() - 1;
				}

				setFromDate(new Date(year, new Date().getMonth() - 1, 1));
				setToDate(new Date(year, new Date().getMonth() - 1, lastDayOfMonth));
			} else if (presetTimeId === "lastWeek") {
				let currentDay = new Date();
				let diff = currentDay.getDate() - currentDay.getDay() + 1;
				let beginningOfWeek = new Date(new Date(currentDay).setDate(diff - 8));
				let endOfWeek = new Date(new Date(currentDay).setDate(diff - 2));

				setFromDate(beginningOfWeek);
				setToDate(endOfWeek);
			} else if (presetTimeId === "allDates") {
				setFromDate(new Date(2001, 0, 1));
				setToDate(new Date(2099, 0, 1));
				// setGrowingSeasonId("all");
			}
		}
	}, [presetTimeId]);

	function setFlightLogCsvInfo() {
		if (flightLogSearchResults && flightLogSearchResults.length > 0) {
			setFlightLogFileName(`FlightLog_${moment().format("YYYYMMDD_hhmma")}.csv`);
			const csvData = _.map(flightLogSearchResults, (r) => {
				let orthoUploadedDates = _.map(
					_.filter(r.orthoImages, (oi) => oi.uploaded),
					"orthoUploadedDateTime",
				);

				let data = {
					Flight: r.name,
					Client: r.clientName.replaceAll(",", ""),
					Cooperators: _.join(
						_.map(r.cooperators, (c) => {
							return c.cooperatorName?.replaceAll(",", "") ?? "";
						}),
						["; "],
					),
					"Flight Date": r.dateAndTime,
					"Flight Plan": r.plan,
					Operator: r.operator,
					Altitude: r.altitude,
					Farms: _.join(
						_.map(r.farms, (f) => {
							return f.name;
						}),
						["; "],
					),
					Fields: r.fields,
					UAS: r.uas,
					"Growing Seasons": _.join(
						_.map(r.growingSeasons, (gs) => {
							return gs.name;
						}),
						["; "],
					),
					"Image Upload": _.join(
						_.map(
							_.filter(r.orthoImages, (oi) => {
								return oi.isUploading === true || oi.uploaded;
							}),
							(oi) => {
								return oi.abbreviation;
							},
						),
						["; "],
					),
					"Plot Analysis": r.plotAnalysis,
					"Drone Speed": userAuth.isApAdmin ? r.droneSpeed : null,
					"Wind Speed": userAuth.isApAdmin ? r.windSpeed : null,
					"Cloud Coverage": userAuth.isApAdmin ? r.cloudCoverage : null,
					"In Flight Pauses": userAuth.isApAdmin
						? r.inFlightPauses === true
							? "Yes"
							: r.inFlightPauses === false
								? "No"
								: null
						: null,
					"Upload Failed": userAuth.isApAdmin && _.some(r.orthoImages, (oi) => oi.uploadFailed) ? "Yes" : null,
					"Mapbox Upload Failed":
						userAuth.isApAdmin && _.some(r.orthoImages, (oi) => oi.mapboxUploadFailed) ? "Yes" : null,
					"Flight Creation Date": moment(r.createdDateTimeUtc).format("MM/DD/YYYY hh:mma"),
					"Orthos Uploaded Date":
						orthoUploadedDates.length > 0 ? moment(orthoUploadedDates[0]).format("MM/DD/YYYY hh:mma") : null,
					"First Analysis Ran Date": r.analysisRanTimings?.firstRunTime
						? moment(r.analysisRanTimings.firstRunTime).format("MM/DD/YYYY hh:mma")
						: null,
					"Last Analysis Ran Date": r.analysisRanTimings?.lastRunTime
						? moment(r.analysisRanTimings.lastRunTime).format("MM/DD/YYYY hh:mma")
						: null,
					"First Field Added Date": r.fieldAddedTimings?.firstAddedTime
						? moment(r.fieldAddedTimings.firstAddedTime).format("MM/DD/YYYY hh:mma")
						: null,
					"Last Field Added Date": r.fieldAddedTimings?.lastAddedTime
						? moment(r.fieldAddedTimings.lastAddedTime).format("MM/DD/YYYY hh:mma")
						: null,
					"First Field Added By": r.fieldAddedTimings?.firstAddedBy,
					"Last Field Added By": r.fieldAddedTimings?.lastAddedBy,
					Link: r.link,
				};
				return data;
			});
			setFlightLogCsvData(csvData);
		}
	}

	return loading ? (
		<Loader active />
	) : !loading && !loadingSearchResults && !flightLogSearchResults ? (
		<Segment basic style={{ width: "100%", margin: 15 }}>
			<Segment>
				<p>{"Flight Log could not be loaded. Please try again. If the problem persists, please contact support."}</p>
			</Segment>
		</Segment>
	) : (
		<Segment basic style={{ display: "flex", flexDirection: "column", height: "100%", padding: "0" }}>
			<SearchPanel onReset={resetFilter}>
				<SearchPanel.Bar
					searchTerm={flightName}
					setSearchTerm={setFlightName}
					placeholder="Search Flights"
					disabled={!flightLogSearchResults}
				/>
				<SearchPanel.MultiDropdown
					placeholder="Growing Seasons"
					options={growingSeasonOptions ?? []}
					defaultOptionIds={growingSeasons}
					setFilters={handleFilters.growingSeasons}
				/>
				<SearchPanel.MultiDropdown
					placeholder="Farms"
					size="small"
					options={farmOptions ?? []}
					setFilters={handleFilters.farms}
				/>
				<SearchPanel.MultiDropdown
					placeholder="Operator"
					size="small"
					options={operatorOptions ?? []}
					setFilters={handleFilters.operator}
				/>
				<SearchPanel.MultiDropdown
					placeholder="Analysis Status"
					size="small"
					options={analysisStatusOptions ?? []}
					setFilters={handleFilters.analysisStatus}
				/>
				<SearchPanel.AdvancedPopup
					filters={filters}
					advancedHeaders={["dateAndTime", "uas", "cooperators", "flightConditions"]}
				>
					<SearchPanel.Date label="Flight Dates From" placeholder="From" date={fromDate} setDate={setFromDate} />
					<SearchPanel.Date label="Flight Dates To" placeholder="To" date={toDate} setDate={setToDate} />
					<SearchPanel.Dropdown
						label="Preset Date Range"
						placeholder="Date Range"
						options={presetTimeOptions ?? []}
						setSelection={setPresetTimeId}
						defaultOption={presetTimeOptions[presetTimeOptions.length - 1]} // all dates
					/>
					<SearchPanel.MultiDropdown placeholder="UAS" options={uasOptions ?? []} setFilters={handleFilters.uas} />
					<SearchPanel.MultiDropdown
						placeholder="Cooperator"
						options={cooperatorCompanyOptions ?? []}
						setFilters={handleFilters.cooperators}
					/>
					<SearchPanel.MultiDropdown
						placeholder="Cloud Coverage"
						options={cloudCoverageTypes ?? []}
						setFilters={handleFilters.cloudCoverage}
					/>
					<SearchPanel.Checkbox
						label="Search All Clients"
						checked={searchAllClients}
						setChecked={setSearchAllClients}
					/>
				</SearchPanel.AdvancedPopup>
			</SearchPanel>

			<Segment basic style={{ margin: 0, paddingTop: 0, paddingBottom: 0 }}>
				{flightLogCsvData && flightLogCsvData.length > 0 && (
					<span style={{ float: "right", textAlign: "right" }}>
						{flightLogCsvData && flightLogCsvData.length > 0 && (
							<CSVLink filename={flightLogFileName} data={flightLogCsvData} enclosingCharacter={""}>
								Download
							</CSVLink>
						)}
					</span>
				)}
			</Segment>
			<FlightSearchResults
				data={flightLogSearchResults ?? []}
				loading={loadingSearchResults}
				filters={filters}
				searchTerm={{ term: flightName, cols: ["name"] }}
			/>
		</Segment>
	);
};

export default FlightLog;
